// A extração de texto de um PDF, no NAVEGADOR.
//
// Porta `src/lib/pdf-utils.ts` da origem: página por página, com o marcador
// `[Página N]` antes de cada uma. O marcador não é enfeite — é o que permite ao
// prompt do chat exigir a citação "(pág. N)" do trecho mais próximo, que é a
// regra mais forte daquele prompt.
//
// ── Por que a extração fica no CLIENTE, e não numa Edge Function ────────────
//
// O Gemini lê PDF nativamente, e é assim que a análise da secretaria funciona
// (`analisar-documento` manda o arquivo inline em base64). Aqui é diferente por
// duas razões medidas:
//
// 1. **O chat precisa do texto a CADA pergunta.** Reenviar o PDF em cada
//    mensagem custaria a requisição inteira de novo e bateria no teto de 20 MiB
//    de `inlineData`. Extrair uma vez e guardar o texto é o que sustenta a
//    conversa.
// 2. **O arquivo não sobe.** Decisão de Victor em 02/09/2026: guardamos análise
//    e texto, nunca o PDF — processo judicial traz partes e valores de terceiros
//    que não são alunos da casa. Extrair no cliente é o que torna isso possível:
//    o arquivo nem chega ao servidor.
//
// ── O worker vem do BUNDLE, não de um CDN ──────────────────────────────────
//
// A origem aponta o worker para `cdnjs.cloudflare.com`
// (`pdf-utils.ts`, linha 4). Aqui ele é importado pelo Vite com `?url`: o hub
// não carrega script de terceiro em página com sessão do aluno, e um CDN fora do
// ar deixaria a ferramenta inteira sem funcionar sem nenhuma pista do motivo.
//
// ── E o pdf.js entra por `import()`, nunca no topo ─────────────────────────
//
// MEDIDO em 02/09, ao construir esta tela: com `import` estático no topo, o
// bundle principal foi de **1.348 kB para 1.735 kB** (374 → 488 kB comprimidos).
// São 387 kB que TODO aluno do hub baixaria — os 62 mil — para uma biblioteca
// que só serve aos 4.086 da Perícia, e só quando um deles envia um PDF.
//
// É a mesma decisão de `lib/mapaBrasil.ts`, carregado por `import()` só por quem
// abre o mapa. O preço é a primeira leitura esperar o chunk chegar, e ela já
// espera a leitura do PDF de todo modo.
type Pdfjs = typeof import("pdfjs-dist");

let modulo: Promise<Pdfjs> | null = null;

/** Carrega o pdf.js uma vez e reaproveita — inclusive entre arquivos do mesmo envio. */
function carregarPdfjs(): Promise<Pdfjs> {
  if (!modulo) {
    modulo = (async () => {
      const [pdfjs, worker] = await Promise.all([
        import("pdfjs-dist"),
        import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
      ]);
      pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
      return pdfjs;
    })();
  }
  return modulo;
}

export type Extracao = {
  /** O texto com os marcadores `[Página N]`. */
  texto: string;
  paginas: number;
  caracteres: number;
};

/** A situação nomeada de uma falha de leitura — o aluno precisa saber QUAL é. */
export type FalhaDeLeitura =
  | "protegido"
  | "corrompido"
  | "sem_texto"
  | "falha";

export class ErroDePdf extends Error {
  constructor(readonly situacao: FalhaDeLeitura, mensagem: string) {
    super(mensagem);
    this.name = "ErroDePdf";
  }
}

/** A frase que o aluno lê, por situação. Nomear a causa é o mínimo: "não deu
 *  certo" manda o perito tentar de novo o que nunca vai funcionar. */
export const EXPLICACAO: Record<FalhaDeLeitura, string> = {
  protegido:
    "Este PDF está protegido por senha. Abra-o no seu leitor, salve uma cópia " +
    "sem senha e envie a cópia.",
  corrompido:
    "Não consegui abrir este arquivo como PDF. Confira se o download terminou " +
    "e se ele abre no seu computador.",
  sem_texto:
    "Este PDF não tem texto para extrair — provavelmente é a digitalização de um " +
    "papel, ou seja, uma imagem. Peça a versão em texto no sistema do tribunal.",
  falha: "Não consegui ler este arquivo. Tente enviá-lo de novo.",
};

/**
 * Extrai o texto de um PDF, marcando cada página.
 *
 * A montagem de linha é a da origem, verbatim no critério: os itens de texto do
 * pdf.js vêm em fragmentos, e `hasEOL` diz onde a linha acaba. Sem isso o texto
 * sai como um parágrafo de milhares de palavras e a IA perde a estrutura de
 * quesito numerado, que é justamente o que ela precisa achar.
 */
export async function extrairTexto(
  arquivo: File,
  aoProgredir?: (pagina: number, de: number) => void,
): Promise<Extracao> {
  const pdfjs = await carregarPdfjs();

  let pdf: Awaited<ReturnType<typeof pdfjs.getDocument>["promise"]>;
  try {
    const bytes = await arquivo.arrayBuffer();
    pdf = await pdfjs.getDocument({ data: bytes }).promise;
  } catch (e) {
    const msg = String((e as Error)?.name ?? "") + String((e as Error)?.message ?? "");
    // O pdf.js tem exceção própria para senha (`PasswordException`). Nomear a
    // causa é o que separa "reenvie sem senha" de "tente de novo" — e a segunda
    // instrução, dada a quem tem um PDF protegido, é um laço.
    if (/password/i.test(msg)) {
      throw new ErroDePdf("protegido", EXPLICACAO.protegido);
    }
    if (/InvalidPDF|structure/i.test(msg)) {
      throw new ErroDePdf("corrompido", EXPLICACAO.corrompido);
    }
    throw new ErroDePdf("falha", EXPLICACAO.falha);
  }

  const total = pdf.numPages;
  const paginas: string[] = [];

  for (let i = 1; i <= total; i++) {
    const pagina = await pdf.getPage(i);
    const conteudo = await pagina.getTextContent();
    const linhas: string[] = [];
    let atual = "";

    for (const item of conteudo.items as { str?: string; hasEOL?: boolean }[]) {
      if (typeof item.str !== "string") continue;
      if (atual.length > 0 && item.str.length > 0) atual += " ";
      atual += item.str;
      if (item.hasEOL) {
        linhas.push(atual.trimEnd());
        atual = "";
      }
    }
    if (atual.trim()) linhas.push(atual.trimEnd());

    paginas.push(`[Página ${i}]\n${linhas.join("\n")}`);
    aoProgredir?.(i, total);
  }

  const texto = paginas.join("\n\n");

  // Um PDF que é só imagem devolve páginas com o marcador e mais nada. Isso NÃO
  // é uma falha nossa e não pode virar "não deu certo": é a digitalização de um
  // papel, e o perito precisa pedir a versão em texto no sistema do tribunal.
  //
  // O corte compara o texto SEM os marcadores: 3.000 páginas de imagem geram
  // ~36 mil caracteres só de "[Página N]", o que passaria por qualquer teste de
  // "tem texto?" feito sobre o total.
  const semMarcadores = texto.replace(/\[Página \d+\]/g, "").trim();
  if (semMarcadores.length < 40) {
    throw new ErroDePdf("sem_texto", EXPLICACAO.sem_texto);
  }

  return { texto, paginas: total, caracteres: texto.length };
}
